// =============================================================
// DamIntakeQueue.jsx — the triage queue: files an ingest brought in that
// nobody has filed yet. Part of the removable DAM module.
//
// These are deliberately NOT shown in the library, and are not "Uncollected"
// either — uncollected means a person looked at an asset and chose to leave it
// out of every collection, which is a claim nobody has made about these. See
// api/src/damIntake.js.
//
// THE UNIT OF WORK IS A FOLDER, NOT A FILE. A drop is a folder of files and the
// decision being asked for is one decision about that folder ("this shoot goes
// in that collection"), so the queue groups by the folder each file arrived in
// and every action is per group. Filing 200 photos one at a time is not a
// workflow anybody completes.
// =============================================================
import { useState } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { Btn, Icon, Spinner, EmptyState, Badge, useToast } from '../../components/ui';
import { formatBytes, categoryOf, iconForCategory, fileIntake, discardIntake } from './damApi';

// How many thumbnails a group shows before collapsing to a count. A folder can
// hold hundreds of files and rendering every tile turns the page people use to
// clear the queue into the slowest page in the app.
const PREVIEW_TILES = 8;

export function DamIntakeQueue({ data, collections = [], loading, onChanged }) {
  const { toast } = useToast();
  const [busyFolder, setBusyFolder] = useState(null);

  if (loading) return <div style={{ padding: space.xxxl }}><Spinner /></div>;

  // A schema that has not picked up migration 188 yet says so by name rather
  // than rendering an empty queue, which would read as "nothing to file".
  if (data && data.available === false) {
    return (
      <div style={{ marginTop: space.xl }}>
        <EmptyState icon="folder" title="Intake triage is not available yet"
          message={data.reason || 'An admin needs to run the Digital Assets schema bootstrap.'} />
      </div>
    );
  }

  const groups = data?.groups || [];
  if (!groups.length) {
    return (
      <div style={{ marginTop: space.xl }}>
        <EmptyState icon="check" title="Nothing waiting to be filed"
          message="Files dropped into the SharePoint dropbox, or sent through an upload link that names no collection, land here until somebody puts them in a collection." />
      </div>
    );
  }

  const run = async (folderKey, fn, done) => {
    setBusyFolder(folderKey);
    try {
      const res = await fn();
      toast.success(done(res));
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusyFolder(null); }
  };

  return (
    <div style={{ marginTop: space.lg, display: 'flex', flexDirection: 'column', gap: space.md }}>
      <div style={{ fontSize: font.sm, color: B.textMd, lineHeight: 1.5 }}>
        <strong>{data.total} file{data.total === 1 ? '' : 's'}</strong> arrived through an ingest and are waiting
        to be filed. They are held out of the library until you put them in a collection — they are not sitting
        in <em>Uncollected</em>.
        {data.truncated && (
          <> Showing the most recent {data.total} — file some and the rest appear.</>
        )}
      </div>
      {groups.map(g => (
        <IntakeGroup key={g.folderPath || '(root)'} group={g} collections={collections}
          busy={busyFolder === (g.folderPath || '(root)')}
          onFile={(body) => run(g.folderPath || '(root)',
            () => fileIntake({ assetIds: g.assets.map(a => a.assetID), ...body }),
            (res) => `Filed ${res.filed} file${res.filed === 1 ? '' : 's'}`)}
          onDiscard={() => run(g.folderPath || '(root)',
            () => discardIntake(g.assets.map(a => a.assetID)),
            (res) => `Discarded ${res.discarded} file${res.discarded === 1 ? '' : 's'}`)} />
      ))}
    </div>
  );
}

function IntakeGroup({ group, collections, busy, onFile, onDiscard }) {
  // Pre-selected from the folder name: the collection it already matches, else
  // "create one called that". The server does the matching (it owns the naming
  // rule) — the client only renders the answer, so the two cannot disagree
  // about whether a collection with that name already exists.
  const [collectionId, setCollectionId] = useState(group.suggestedCollectionId || '');
  const [newName, setNewName] = useState(
    group.suggestedCollectionId ? '' : (group.suggestedCollectionName || ''));
  const [expanded, setExpanded] = useState(false);

  const assets = group.assets || [];
  const shown = expanded ? assets : assets.slice(0, PREVIEW_TILES);
  const bytes = assets.reduce((n, a) => n + (a.fileSizeBytes || 0), 0);

  // Counts of what the folder said each file is. Worth showing per group,
  // because "these 200 are raw" is the single most useful thing a folder name
  // told us and it is what somebody is about to file on.
  const rawCount = assets.filter(a => a.rawEdited === 'raw').length;
  const editedCount = assets.filter(a => a.rawEdited === 'edited').length;

  const canFile = !!collectionId || !!newName.trim();

  return (
    <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: radius.xxl, padding: space.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
        <Icon name="folder" size={16} color={B.navy} />
        <span style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text }}>
          {group.folderPath || 'No folder (dropped at the top level)'}
        </span>
        <Badge color={B.textMd}>{group.count} file{group.count === 1 ? '' : 's'}</Badge>
        {bytes > 0 && <span style={{ fontSize: font.xs, color: B.textLt }}>{formatBytes(bytes)}</span>}
        {rawCount > 0 && <Badge color={B.navy}>{rawCount} raw</Badge>}
        {editedCount > 0 && <Badge color={B.green}>{editedCount} edited</Badge>}
      </div>

      {/* Thumbnails — enough to recognise the drop, not the whole folder. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: space.md }}>
        {shown.map(a => <IntakeTile key={a.assetID} asset={a} />)}
        {!expanded && assets.length > PREVIEW_TILES && (
          <button type="button" onClick={() => setExpanded(true)}
            style={{ width: 72, height: 72, borderRadius: radius.lg, border: `1px dashed ${B.border}`,
              background: B.surfaceMuted, cursor: 'pointer', fontFamily: 'inherit', fontSize: font.xs,
              fontWeight: weight.semibold, color: B.navy }}>
            +{assets.length - PREVIEW_TILES}
          </button>
        )}
      </div>

      {/* The decision */}
      <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap', marginTop: space.lg }}>
        <select value={collectionId} onChange={e => { setCollectionId(e.target.value); if (e.target.value) setNewName(''); }}
          style={{ padding: '7px 10px', fontSize: font.sm, fontFamily: 'inherit', cursor: 'pointer',
            border: `1px solid ${collectionId ? B.navy : B.border}`, borderRadius: radius.lg,
            background: B.surface, color: B.text, outline: 'none', maxWidth: 260 }}>
          <option value="">Choose a collection…</option>
          {collections.map(c => <option key={c.collectionID} value={c.collectionID}>{c.name}</option>)}
        </select>
        <span style={{ fontSize: font.xs, color: B.textLt }}>or</span>
        <input value={newName} onChange={e => { setNewName(e.target.value); if (e.target.value) setCollectionId(''); }}
          placeholder="New collection name"
          style={{ padding: '7px 10px', fontSize: font.sm, fontFamily: 'inherit', minWidth: 200,
            border: `1px solid ${B.border}`, borderRadius: radius.lg, color: B.text, outline: 'none' }} />
        <Btn onClick={() => onFile(collectionId ? { collectionId } : { newCollectionName: newName.trim() })}
          disabled={!canFile || busy} loading={busy}>
          <Icon name="check" size={14} /> File {group.count}
        </Btn>
        <button type="button" onClick={onDiscard} disabled={busy}
          style={{ border: 'none', background: 'transparent', cursor: busy ? 'default' : 'pointer',
            fontFamily: 'inherit', fontSize: font.sm, fontWeight: weight.medium, color: B.textMd }}>
          Discard
        </button>
      </div>
      {group.suggestedCollectionName && (
        <div style={{ marginTop: 6, fontSize: font.xs, color: B.textLt }}>
          {group.suggestedCollectionId
            ? `Suggested from the folder name — “${group.suggestedCollectionName}” already exists.`
            : `Suggested from the folder name — no collection called “${group.suggestedCollectionName}” yet.`}
        </div>
      )}
    </div>
  );
}

function IntakeTile({ asset }) {
  const cat = categoryOf(asset.fileType);
  return (
    <div title={`${asset.fileName}${asset.rawEdited ? ` · ${asset.rawEdited}` : ''}`}
      style={{ width: 72, height: 72, borderRadius: radius.lg, overflow: 'hidden', background: B.surfaceSunken,
        border: `1px solid ${B.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {asset.thumbnailUrl
        ? <img src={asset.thumbnailUrl} alt={asset.fileName} loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : <Icon name={iconForCategory(cat)} size={22} color={B.textLt} />}
    </div>
  );
}
